import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import emailjs from '@emailjs/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

const AboutUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const emailResponse = await emailjs.send(
        'service_3tx8jli',
        'template_2nq3cmj',
        {
          name: formData.name,
          email: formData.email,
          mobile: formData.phone, // 'phone' को 'mobile' के रूप में मैप करें
          subject: formData.subject,
          message: formData.message,
        },
        '2znub3nsdivNcJHnz'
      );

      console.log('Email sent successfully:', emailResponse.status, emailResponse.text);
      setSuccessMessage('Thank you for your enquiry! We will get back to you soon.');
      setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error('Error sending email:', err);
      setErrorMessage('Error sending enquiry. Please try again.');
    }
  };

  // Show modal and auto-close after 3 seconds
  React.useEffect(() => {
    if (successMessage || errorMessage) {
      setShowModal(true);
      const timer = setTimeout(() => {
        setShowModal(false);
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  return (
    <div className="sf-about">
      {/* About the Company Section */}
      <section className="sf-about-story tw-py-[clamp(3rem,6vw,5rem)]">
        <Container>
          <div className="tw-grid tw-items-center tw-gap-8 md:tw-grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:tw-gap-12">
            <div>
              <p className="sf-about-eyebrow">Our story</p>
              <h1>About Srisailam Foundation</h1>
              <p className="sf-about-lead">Srisailam Foundation currently provides free education and learning support for children studying up to Class 8, helping them build a stronger foundation for their future.</p>
              <p>We believe opportunity begins with access to learning. As the Foundation grows, we aspire to explore skill development, health, women empowerment, environment and livelihood as future areas of community development.</p>
            </div>
            <aside className="sf-about-card" aria-labelledby="about-current-focus">
              <h2 id="about-current-focus">Our Current Focus</h2>
              <div className="tw-mb-4 tw-h-1 tw-w-12 tw-rounded-full tw-bg-sf-teal" aria-hidden="true" />
              <ul className="tw-m-0 tw-list-none tw-space-y-3 tw-p-0">
                <li className="tw-flex tw-gap-3"><span className="tw-mt-2 tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-sf-gold" aria-hidden="true" />Free education and learning support</li>
                <li className="tw-flex tw-gap-3"><span className="tw-mt-2 tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-sf-gold" aria-hidden="true" />Children studying up to Class 8</li>
                <li className="tw-flex tw-gap-3"><span className="tw-mt-2 tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-sf-gold" aria-hidden="true" />Raebareli, Uttar Pradesh</li>
              </ul>
            </aside>
          </div>
        </Container>
      </section>

      {/* Mission and Vision Section */}
      <section className="sf-about-mission tw-py-[clamp(2.8rem,5vw,4.4rem)]">
        <Container>
          <Row className="g-4">
            <Col md={6}>
              <div className="sf-about-card tw-h-full">
                <h2>Our Mission</h2>
                <p>
                  To support children studying up to Class 8 through free education and learning opportunities, helping them build confidence and develop their potential.
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="sf-about-card tw-h-full">
                <h2>Our Vision</h2>
                <p>
                  To build a nation where every individual has access to quality education, essential skills, and equal opportunities—fostering innovation, cultural enrichment, and sustainable development across all communities in India.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Enquiry Form Section */}
      <section className="sf-about-enquiry tw-py-[clamp(2.8rem,5vw,4.4rem)]">
        <Container>
          <p className="sf-about-eyebrow">Connect with us</p>
          <h2 className="tw-mb-8">Enquire Now</h2>
          <Row className="g-4">
            {/* Left Side: Content */}
            <Col md={6}>
              <div className="sf-about-card tw-h-full">
                <h3>Why Contact Us?</h3>
                <p>
                  We’re here to support you—whether you have questions, wish to contribute, or want to get involved. Reach out to learn more about our work and how you can make a difference.
                </p>
                <p>Here’s how we can help:</p>
                <ul className="sf-about-list">
                  <li>Learn more about our current education work and future vision.</li>
                  <li>Discover ways to contribute or collaborate.</li>
                  <li>Join us as a volunteer and be part of the impact.</li>
                  <li>Support our cause through donations.</li>
                </ul>
              </div>
            </Col>

            {/* Right Side: Form */}
            <Col md={6}>
              <div className="sf-about-card sf-about-form tw-h-full">
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formName">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formPhone">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter your phone number"
                          pattern="[0-9]{10}"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="formSubject">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                          as="select"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select a subject</option>
                          <option value="Donate Enquiry">Donate Enquiry</option>
                          <option value="Add Volunteers">Add Volunteers</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="formMessage">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your message"
                      required
                    />
                  </Form.Group>

                  <div className="text-center">
                    <Button variant="danger" type="submit" className="enquiry-btn">
                      Submit Enquiry
                    </Button>
                  </div>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Success/Error Modal */}
      <Modal show={showModal} centered>
        <Modal.Body className="text-center">
          {successMessage ? (
            <div className="d-flex flex-column align-items-center">
              <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
              <h4 className="text-success mb-2">Success!</h4>
              <p>{successMessage}</p>
            </div>
          ) : (
            <div className="d-flex flex-column align-items-center">
              <FontAwesomeIcon icon={faTimesCircle} size="3x" className="text-danger mb-3" />
              <h4 className="text-danger mb-2">Error!</h4>
              <p>{errorMessage}</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AboutUs;
